import { useCallback, useEffect, useState } from "react"
import { AlertCircle, AlertTriangle, CheckCircle2, Clock3, ExternalLink, Info, RefreshCw, XCircle } from "lucide-react"
import { apiGet, API_ENDPOINTS } from "../config/api"
import { useLanguage } from "../i18n/LanguageContext"

const initialData = { summary: { critical: 0, warning: 0, information: 0, total: 0 }, exceptions: [], generated_at: null }

const severityStyles = {
  critical: { panel: "exceptions-item-critical", icon: XCircle, iconClass: "text-red-600", label: "critical" },
  warning: { panel: "exceptions-item-warning", icon: AlertTriangle, iconClass: "text-amber-600", label: "warning" },
  information: { panel: "exceptions-item-information", icon: Info, iconClass: "text-blue-600", label: "information" },
}

export default function BookingExceptions() {
  const { t } = useLanguage()
  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true)
    else setLoading(true)
    try {
      const response = await apiGet(API_ENDPOINTS.OPERATIONS_EXCEPTIONS)
      setData(response || initialData)
      setError("")
    } catch (requestError) {
      setError(requestError.message || t("exceptions.loadFailed"))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [t])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load(true), 15000)
    return () => window.clearInterval(timer)
  }, [load])

  const summaryCards = [
    { key: "critical", value: data.summary?.critical, icon: XCircle, className: "exceptions-summary-critical" },
    { key: "warning", value: data.summary?.warning, icon: AlertTriangle, className: "exceptions-summary-warning" },
    { key: "information", value: data.summary?.information, icon: Info, className: "exceptions-summary-information" },
    { key: "total", value: data.summary?.total, icon: Clock3, className: "exceptions-summary-total" },
  ]

  return (
    <section className="exceptions-page">
      <div className="management-header">
        <div>
          <h2 className="management-title">{t("exceptions.title")}</h2>
          <p className="text-gray-500 mt-1">{t("exceptions.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {data.generated_at && <span className="text-xs text-gray-500">{t("exceptions.updated", { time: new Date(data.generated_at).toLocaleTimeString() })}</span>}
          <button type="button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50" onClick={() => load(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {t("exceptions.refresh")}
          </button>
        </div>
      </div>

      {error && <div className="p-4 mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center gap-2"><AlertCircle className="w-5 h-5" />{error}</div>}

      {loading ? <div className="p-8 text-center text-gray-500">{t("common.loading")}</div> : <>
        <div className="exceptions-summary-grid">
          {summaryCards.map(({ key, value, icon: Icon, className }) => <div key={key} className={`exceptions-summary-card ${className}`}><Icon className="w-5 h-5" /><div><p>{t(`exceptions.${key}`)}</p><strong>{Number(value || 0).toLocaleString()}</strong></div></div>)}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6 p-5">
          <div className="flex items-center justify-between gap-3 mb-4"><div><h3 className="text-lg font-semibold text-gray-800">{t("exceptions.listTitle")}</h3><p className="text-sm text-gray-500">{t("exceptions.listHint")}</p></div><CheckCircle2 className={data.exceptions?.length ? "hidden" : "w-6 h-6 text-green-600"} /></div>
          {data.exceptions?.length ? <div className="space-y-3">{data.exceptions.map((item) => {
            const style = severityStyles[item.severity] || severityStyles.information
            const Icon = style.icon
            return <div key={item.id} className={`exceptions-item ${style.panel}`}>
              <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${style.iconClass}`} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><span className="exceptions-severity">{t(`exceptions.severities.${style.label}`)}</span><strong className="text-gray-800">{t(`exceptions.types.${item.type}`)}</strong>{item.booking_id && <span className="text-sm text-gray-500">#{item.booking_id}</span>}</div>
                <p className="text-sm text-gray-700 mt-1">{item.message}</p>
                <p className="text-xs text-gray-500 mt-2">{item.passenger_name || t("exceptions.unknownPassenger")} · {item.captain_name || t("exceptions.unassignedCaptain")} · {item.age_minutes || 0} {t("exceptions.minutesAgo")}</p>
              </div>
              {item.booking_id && <a className="inline-flex items-center gap-1 text-sm text-amber-700 hover:underline whitespace-nowrap" href={`/bookings?booking_id=${item.booking_id}`}><ExternalLink className="w-4 h-4" />{t("exceptions.openBooking")}</a>}
            </div>
          })}</div> : <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-4"><CheckCircle2 className="w-5 h-5" />{t("exceptions.noExceptions")}</div>}
        </div>
      </>}
    </section>
  )
}
